import React from "react"
import { useTranslation } from "react-i18next"
import { motion } from "framer-motion"
import { ShieldCheck, FileCheck, Lock, HelpCircle } from "lucide-react"
import { useNavigationStore } from "@/stores/navigation"
import AIWorkflow from "@/components/ui/AIWorkflow"

interface SectionProps {
  id: string
  children: React.ReactNode
  className?: string
}

const Section: React.FC<SectionProps> = ({ id, children, className = "" }) => (
  <section
    id={id}
    className={`mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}
  >
    {children}
  </section>
)

const AI: React.FC = () => {
  const { t } = useTranslation()
  const navigationStore = useNavigationStore()

  const aiValueItems = [
    {
      title: t("ai.whereAiDeliversValue.items.intelligentReferralTriage.title"),
      desc: t(
        "ai.whereAiDeliversValue.items.intelligentReferralTriage.description"
      ),
      color: "bg-blue-50 text-blue-700",
    },
    {
      title: t(
        "ai.whereAiDeliversValue.items.smartSchedulingRecommendations.title"
      ),
      desc: t(
        "ai.whereAiDeliversValue.items.smartSchedulingRecommendations.description"
      ),
      color: "bg-green-50 text-green-700",
    },
    {
      title: t("ai.whereAiDeliversValue.items.predictiveAnalytics.title"),
      desc: t("ai.whereAiDeliversValue.items.predictiveAnalytics.description"),
      color: "bg-purple-50 text-purple-700",
    },
    {
      title: t("ai.whereAiDeliversValue.items.adaptiveFormsWorkflows.title"),
      desc: t(
        "ai.whereAiDeliversValue.items.adaptiveFormsWorkflows.description"
      ),
      color: "bg-pink-50 text-pink-700",
    },
    {
      title: t(
        "ai.whereAiDeliversValue.items.operationalDashboardsInsights.title"
      ),
      desc: t(
        "ai.whereAiDeliversValue.items.operationalDashboardsInsights.description"
      ),
      color: "bg-yellow-50 text-yellow-700",
    },
    {
      title: t(
        "ai.whereAiDeliversValue.items.decisionSupportNextBestActions.title"
      ),
      desc: t(
        "ai.whereAiDeliversValue.items.decisionSupportNextBestActions.description"
      ),
      color: "bg-indigo-50 text-indigo-700",
    },
  ]

  const aiInActionItems = [
    {
      title: t("ai.realWorldExample.aiInAction.dataExtraction.title"),
      desc: t("ai.realWorldExample.aiInAction.dataExtraction.description"),
    },
    {
      title: t("ai.realWorldExample.aiInAction.smartRouting.title"),
      desc: t("ai.realWorldExample.aiInAction.smartRouting.description"),
    },
    {
      title: t("ai.realWorldExample.aiInAction.operationalInsights.title"),
      desc: t("ai.realWorldExample.aiInAction.operationalInsights.description"),
    },
  ]

  const faqItems = [
    {
      q: t("ai.faq.items.ehrIntegration.question"),
      a: t("ai.faq.items.ehrIntegration.answer"),
    },
    {
      q: t("ai.faq.items.dataProtection.question"),
      a: t("ai.faq.items.dataProtection.answer"),
    },
    {
      q: t("ai.faq.items.aiOverride.question"),
      a: t("ai.faq.items.aiOverride.answer"),
    },
    {
      q: t("ai.faq.items.gettingStarted.question"),
      a: t("ai.faq.items.gettingStarted.answer"),
    },
  ]

  return (
    <div className="min-h-screen w-full bg-white text-slate-800">
      {/* HERO */}
      <header
        className="w-full text-white relative overflow-hidden"
        style={{
          background:
            "radial-gradient(1000px 600px at -10% 50%, rgba(6,13,34,0.55), rgba(6,13,34,0) 55%), linear-gradient(110deg, #1F2B59 0%, #6B47DC 35%, #FF7A59 70%, #00B3A4 100%)",
        }}
      >
        {/* Hero AI Illustration */}
        <div className="absolute inset-y-0 right-0 z-0 flex w-[55%] items-center justify-center pointer-events-none">
          <svg
            viewBox="0 0 540 540"
            className="h-[420px] w-[420px] md:h-[480px] md:w-[480px] opacity-95"
            aria-hidden
          >
            <defs>
              <linearGradient id="nodes" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#FFFFFF" stopOpacity="1" />
                <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0.95" />
              </linearGradient>
            </defs>
            <g stroke="#FFFFFF" strokeWidth="3.5" fill="none">
              <path d="M130 270 C140 160, 220 120, 270 120 C360 120, 410 180, 420 250 C430 330, 360 400, 270 410 C180 420, 120 360, 130 270 Z" />
              <path d="M170 220 L240 180 L310 210 L360 260 L320 330 L240 350 L190 300 Z" />
              <path d="M240 180 L240 350 M310 210 L320 330 M190 300 L360 260" />
            </g>
            {[
              { x: 240, y: 180 },
              { x: 310, y: 210 },
              { x: 360, y: 260 },
              { x: 320, y: 330 },
              { x: 240, y: 350 },
              { x: 190, y: 300 },
              { x: 170, y: 220 },
            ].map((n, i) => (
              <circle key={i} cx={n.x} cy={n.y} r="10" fill="#FFFFFF" />
            ))}
          </svg>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold leading-tight tracking-tight"
            style={{
              fontFamily:
                "Montserrat, Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
            }}
          >
            {t("ai.hero.title")}
            <sup className="align-super text-xl sm:text-2xl ml-1">™</sup>
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg md:text-xl/8 opacity-95">
            {t("ai.hero.subtitle")}
          </p>
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <button
              onClick={navigationStore.openDemoModal}
              className="w-full sm:w-auto text-center bg-white text-[#395091] font-semibold px-6 py-3 rounded-lg shadow-md hover:bg-gray-100 transition"
            >
              {t("common.bookDemo")}
            </button>
            <a
              href="#demo"
              className="w-full sm:w-auto text-center bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/20 transition"
            >
              {t("ai.hero.seeExample")}
            </a>
          </div>
        </div>
      </header>

      {/* WHERE AI DELIVERS VALUE */}
      <Section id="summary" className="py-8 sm:py-10">
        <div className="rounded-3xl bg-white/60 p-6 shadow-[0_1px_0_rgba(31,43,89,0.06),0_8px_24px_rgba(31,43,89,0.06)]">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6">
            {t("ai.whereAiDeliversValue.title")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {aiValueItems.map((item, idx) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: idx * 0.05 }}
                className={`rounded-2xl border border-[#1F2B59]/10 p-5 shadow-sm hover:shadow-md transition ${item.color}`}
              >
                <h3 className="text-lg font-semibold mb-1">{item.title}</h3>
                <p className="text-sm opacity-90">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* DEMO SNAPSHOT / VIDEO */}
      <Section
        id="demo"
        className="py-12 sm:py-16 bg-gradient-to-br from-[#EEF9FF] via-[#F0F4FA] to-[#E6F7F1] scroll-mt-24 lg:scroll-mt-28"
      >
        <div className="max-w-1xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              {t("ai.demo.title")}
            </h2>
            <p className="mt-2 text-sm sm:text-base text-[#31407A]">
              {t("ai.demo.subtitle")}
            </p>
          </div>
          
          <AIWorkflow />
        </div>
      </Section>

      {/* REAL-WORLD EXAMPLE */}
      <Section id="example" className="py-8 sm:py-10">
        <div className="rounded-3xl border border-[#1F2B59]/10 bg-gradient-to-br from-[#EEF9FF] to-[#E6F7F1] p-6 shadow-sm">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4">
            {t("ai.realWorldExample.title")}
          </h3>
          <div className="grid gap-8 md:grid-cols-2 items-start">
            {/* Narrative */}
            <div>
              <p className="text-[15px] text-[#1F2B59]/90 sm:text-base mb-4">
                {t("ai.realWorldExample.description")}
              </p>
              <div className="mt-4 rounded-xl bg-white p-4 shadow-md border border-[#1F2B59]/10">
                <h4 className="font-semibold text-slate-900 mb-2">
                  {t("ai.realWorldExample.typicalResults")}
                </h4>
                <ul className="space-y-1 text-sm text-[#31407A] list-disc list-inside">
                  {(
                    t("ai.realWorldExample.results", {
                      returnObjects: true,
                    }) as string[]
                  ).map((result: string, index: number) => (
                    <li key={index}>{result}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-slate-500">
                  {t("ai.realWorldExample.basedOnBenchmarks")}
                </p>
              </div>
            </div>

            {/* AI in Action */}
            <div className="space-y-4">
              {aiInActionItems.map((f, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35, delay: idx * 0.05 }}
                  className="rounded-2xl border border-[#1F2B59]/10 bg-[#F7F8FD] p-4 shadow-sm hover:shadow-md"
                >
                  <h4 className="text-sm font-semibold text-slate-900">
                    {f.title}
                  </h4>
                  <p className="mt-1 text-xs text-[#31407A]">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* TRUSTED & COMPLIANT */}
      <Section
        id="trusted"
        className="border-t border-slate-200 py-12 sm:py-14"
      >
        <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6 text-center">
          {t("ai.trustedCompliant.title")}
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.hipaaAligned")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <FileCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.soc2Type2")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <Lock className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.ssoSaml")}
            </span>
          </div>
          <div className="flex flex-col items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#395091]" />
            <span className="text-slate-700 font-medium">
              {t("ai.trustedCompliant.gdprDpf")}
            </span>
          </div>
        </div>
      </Section>

      {/* FAQ SECTION */}
      <Section id="faq" className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center">
            {t("ai.faq.title")}
          </h2>
          <div className="mt-8 space-y-6">
            {faqItems.map((item, i) => (
              <div
                key={i}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-5 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <HelpCircle className="h-5 w-5 text-[#395091] mt-1" />
                  <div>
                    <h3 className="font-semibold text-slate-900">{item.q}</h3>
                    <p className="mt-1 text-sm text-slate-700">{item.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  )
}

export default AI
